<template>
  <section>
    <b-col
      v-if="isLoading"
      class="px-0"
    >
      <b-skeleton
        animation="wave"
        class="mx-auto"
        width="100%"
        height="325px"
      />
      <b-skeleton
        animation="wave"
        class="mx-auto"
        width="100%"
        height="606px"
      />
      <b-skeleton
        animation="wave"
        class="mx-auto"
        width="100%"
        height="660px"
      />
      <b-skeleton
        animation="wave"
        class="mx-auto"
        width="100%"
        height="476px"
      />
    </b-col>
    <scroll-container
      v-else
      class="pt-2 mt-n30px mr-n15px"
      height-offset="125px"
    >
      <div class="pr-1">
        <b-card class="mb-2 mt-1">
          <h5>{{ $tk('ClientPortal.Admin.Email.Informations') }}</h5>
          <b-form-group
            class="my-2"
            :label="$tk('ClientPortal.Admin.SubtypeName')"
          >
            <b-form-input
              v-model="email.emailTemplate.name"
              size="sm"
              type="text"
            />
          </b-form-group>
          <b-form-group
            class="mb-0"
            :label="$tk('ClientPortal.Form.Description')"
          >
            <b-form-textarea
              v-model="email.emailTemplate.description"
              type="text"
              no-resize
            />
          </b-form-group>
        </b-card>
        <b-card
          class="mb-2"
        >
          <h5>{{ email.emailTemplate.name }} {{ $tk('ClientPortal.Admin.Email.Properties') }}</h5>
          <div
            v-for="(item,index) in email.properties"
            :key="item.propertyName"
            class="mt-2"
          >
            <b-form-group
              :label="item.propertyName"
              class="mb-0"
            >
              <b-form-input
                v-model="email.properties[index].previewValue"
                type="text"
                size="sm"
                @keyup="updatePreview"
              />
            </b-form-group>
          </div>
        </b-card>
        <b-card
          class="mb-2"
        >
          <b-tabs>
            <b-tab
              active
            >
              <template #title>
                <GjIcon
                  key="desktop"
                  name="Desktop"
                />
                {{ $tk('ClientPortal.Form.AdPreview.Desktop') }}
              </template>
              <div class="mt-2 mb-1">
                {{ email.emailTemplate.name }} - {{ $tk('ClientPortal.Form.AdPreview.DesktopPreview') }}
              </div>
              <div class="w-100 border bg-white">
                <iframe
                  :srcdoc="model.htmlContent"
                  style="border: 0px; width: 100%; min-height: 560px;"
                />
              </div>
            </b-tab>
            <b-tab>
              <template #title>
                <GjIcon
                  key="mobile"
                  name="Mobile"
                />
                {{ $tk('ClientPortal.Form.AdPreview.Mobile') }}
              </template>
              <b-container
                class="container-372px"
              >
                <div class="mt-2 mb-1">
                  {{ email.emailTemplate.name }} - {{ $tk('ClientPortal.Form.AdPreview.MobilePreview') }}
                </div>
                <div class="border bg-white">
                  <iframe
                    :srcdoc="model.htmlContent"
                    style="border: 0px; width: 100%; min-height: 580px;"
                  />
                </div>
              </b-container>
            </b-tab>
          </b-tabs>
        </b-card>
        <b-card
          class="mb-2"
        >
          <div class="mb-2">
            {{ email.emailTemplate.name }} {{ $tk("ClientPortal.Admin.TemplateEditor") }}
          </div>
          <AceEditor
            ref="aceEditor"
            v-model="email.emailTemplate.htmlContent"
            :value="email.emailTemplate.htmlContent"
            lang="javascript"
            theme="github"
            width="100%"
            height="377px"
            :options="options"
            :commands="commands"
            @init="editorInit"
          />
        </b-card>
      </div>
    </scroll-container>
    <div class="border-top text-right py-1">
      <b-button
        v-b-modal.modal-send-email
        class="bg-white"
        size="sm"
        variant="outline-dark"
      >
        {{ $tk('ClientPortal.Admin.SendEmailForTestingPurpose') }}
      </b-button>
      <b-button
        class="ml-50"
        size="sm"
        variant="primary"
        :disabled="saving"
        @click="saveChanges"
      >
        {{ $tk('ClientPortal.Admin.SaveChanges') }}
      </b-button>
    </div>
    <b-modal
      id="modal-send-email"
      :ok-title="$tk('ClientPortal.Form.Actions.Send')"
      :cancel-title="$tk('ClientPortal.Form.Actions.Cancel')"
      centered
      cancel-variant="outline-secondary"
      size="md"
      :title="$tk('ClientPortal.Admin.SendEmailForTestingPurpose')"
      no-enforce-focus
      @change="resetModalData"
      @ok="sendEmailForTestingPurpose"
    >
      <b-form-group
        :label="$tk('ClientPortal.Form.Email')"
        label-size="xs"
        class="mb-0"
      >
        <b-form-input
          v-model="testEmail"
          size="sm"
          type="email"
        />
      </b-form-group>
    </b-modal>
  </section>

</template>

<script>
import {
  computed,
  onMounted,
  ref,
  reactive,
} from '@vue/composition-api'
import store from '@/store'
import AceEditor from 'vuejs-ace-editor'
import ScrollContainer from '@/components/ScrollContainer.vue'
import EmailsService from '@/api/admin/emails.service'
import translation from '@/mixins/translate'
import GjIcon from '@gjirafatech/gjirafa-icons/Icon.vue'
import notification from '@/mixins/toastification'

export default {
  components: {
    GjIcon,
    AceEditor,
    ScrollContainer,
  },
  mixins: [notification],
  setup(_, context) {
    const email = computed(() => store.getters.getEmail)
    const isLoading = ref(true)
    const options = ref({
      enableBasicAutocompletion: true,
      enableLiveAutocompletion: true,
      fontSize: 14,
      highlightActiveLine: true,
      enableSnippets: true,
      showLineNumbers: true,
      tabSize: 2,
      showPrintMargin: false,
      showGutter: true,
    })
    const commands = ref([
      {
        name: 'save',
        bindKey: { win: 'Ctrl-s', mac: 'Command-s' },
        readOnly: true,
      },
    ])
    const model = reactive({
      htmlContent: null,
    })
    const { id } = context.root.$route.params
    const saving = ref(false)
    const testEmail = ref(null)

    const updatePreview = () => {
      let value = email.value.emailTemplate.htmlContent
      if (email.value.properties !== null) {
        email.value.properties.forEach(
          property => {
            const name = property.propertyName
            const { previewValue } = property
            value = value.replace(`{{${name}}}`, previewValue)
          },
        )
      }
      model.htmlContent = value
    }
    const editorInit = editor => {
      // eslint-disable-next-line global-require
      require('brace/ext/language_tools') // language extension prerequsite...
      // eslint-disable-next-line global-require
      require('brace/mode/markdown')
      // eslint-disable-next-line global-require
      require('brace/mode/javascript') // language
      // eslint-disable-next-line global-require
      require('brace/mode/less')
      // eslint-disable-next-line global-require
      require('brace/theme/github')
      // eslint-disable-next-line global-require
      require('brace/snippets/javascript') // snippet
      editor.session.on('change', () => {
        updatePreview()
      })
    }

    onMounted(async () => {
      isLoading.value = true
      await store.dispatch('getEmail', id)
      isLoading.value = false
    })

    const resetModalData = () => {
      testEmail.value = null
    }

    const sendEmailForTestingPurpose = async () => {
      try {
        const response = await EmailsService.sendEmail(email.value.properties, testEmail.value, id)
        if (response) {
          notification.methods.success(translation.methods.$tk('Validation.ContactEmail.EmailSentSuccesfully'))
        } else {
          notification.methods.error(translation.methods.$tk('Validation.ContactEmail.EmailFailedToBeSent'))
        }
      } catch (err) {
        notification.methods.error(err.message)
      }
    }
    const saveChanges = async () => {
      saving.value = true
      try {
        const response = await EmailsService.updateEmailTemplate(email.value.emailTemplate)
        if (response) {
          notification.methods.success(translation.methods.$tk('ClientPortal.Admin.ChangesHaveBeenSavedSuccesfully'))
        } else {
          notification.methods.error(translation.methods.$tk('ClientPortal.Admin.ErrorSavingChanges'))
        }
      } catch (err) {
        notification.methods.error(err.message)
      } finally {
        saving.value = false
      }
    }

    return {
      email,
      options,
      commands,
      isLoading,
      editorInit,
      updatePreview,
      sendEmailForTestingPurpose,
      model,
      testEmail,
      resetModalData,
      saveChanges,
      saving,
    }
  },
}
</script>
